import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { getDocuments, downloadDocument } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Filter,
  File,
  Image,
  FileSpreadsheet,
  Search,
  User,
  FolderOpen,
  Tag
} from 'lucide-react';



const getFileIcon = (fileType) => {
  switch (fileType?.toLowerCase()) {
    case 'pdf':
      return FileText;
    case 'jpg':
    case 'jpeg':
    case 'png':
      return Image;
    case 'xlsx':
    case 'xls':
    case 'csv':
      return FileSpreadsheet;
    default:
      return File;
  }
};

const DocumentsList = () => {
  const { t, locale } = useLanguage();
  const { isAdmin } = useAuth();
  const categoryLabels = t('portalUi.client.documents.categories', {
    valsts_institucijam: 'Valsts institūcijām',
    atskaites: 'Atskaites',
    merijumu_dati: 'Mērījumu dati',
    skices: 'Skices',
    cits: 'Citi'
  });
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [uploaderFilter, setUploaderFilter] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [showLatestOnly, setShowLatestOnly] = useState(false);
  const [search, setSearch] = useState('');

  const fetchDocuments = useCallback(async () => {
    try {
      const data = await getDocuments();
      setDocuments(data);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const projectOptions = useMemo(() => {
    const map = new Map();
    documents.forEach((doc) => {
      if (doc.project_id) {
        map.set(doc.project_id, doc.project_name || 'Projekts');
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [documents]);

  const uploaderOptions = useMemo(() => {
    const map = new Map();
    documents.forEach((doc) => {
      if (doc.uploaded_by_name) {
        map.set(doc.uploaded_by_name, doc.uploaded_by_name);
      }
    });
    return Array.from(map.values());
  }, [documents]);

  const latestVersionMap = useMemo(() => {
    const map = new Map();
    documents.forEach((doc) => {
      const key = `${doc.project_id || 'no-project'}-${doc.original_filename}`;
      const current = map.get(key) || 0;
      if ((doc.version || 1) > current) {
        map.set(key, doc.version || 1);
      }
    });
    return map;
  }, [documents]);

  const normalizedSearch = search.trim().toLowerCase();

  const filteredDocs = useMemo(() => {
    const result = documents.filter((doc) => {
      const matchesCategory = categoryFilter === 'all' || doc.category === categoryFilter;
      const matchesProject = projectFilter === 'all' || doc.project_id === projectFilter;
      const matchesUploader = uploaderFilter === 'all' || doc.uploaded_by_name === uploaderFilter;
      const key = `${doc.project_id || 'no-project'}-${doc.original_filename}`;
      const matchesLatest = !showLatestOnly || (doc.version || 1) === latestVersionMap.get(key);
      const matchesSearch = !normalizedSearch || [
        doc.original_filename,
        doc.project_name,
        doc.uploaded_by_name
      ].some((value) => value?.toLowerCase().includes(normalizedSearch));

      return matchesCategory && matchesProject && matchesUploader && matchesLatest && matchesSearch;
    });

    return result.sort((a, b) => {
      const aDate = new Date(a.created_at).getTime();
      const bDate = new Date(b.created_at).getTime();
      return sortOrder === 'newest' ? bDate - aDate : aDate - bDate;
    });
  }, [documents, categoryFilter, projectFilter, uploaderFilter, showLatestOnly, normalizedSearch, latestVersionMap, sortOrder]);

  const handleDownload = async (doc) => {
    try {
      await downloadDocument(doc.id, doc.original_filename);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error(t('portalUi.client.documents.downloadError', 'Neizdevās lejupielādēt dokumentu'));
    }
  };

  const emptyStateMessage = documents.length === 0
    ? t('portalUi.client.documents.emptyNoDocuments', 'Šobrīd nav pievienotu dokumentu.')
    : t('portalUi.client.documents.emptyFiltered', 'Nav atrasti dokumenti pēc izvēlētajiem filtriem.');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#1F3D2B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-documents">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-[#2E2E2E]" data-testid="client-documents-title"><span className="gradient-text">{t('portalUi.client.documents.title', 'Dokumenti')}</span></h1>
        <p className="text-[#2E2E2E]/60 mt-1" data-testid="client-documents-subtitle">
          {t('portalUi.client.documents.subtitlePrefix', 'Apskatiet un lejuplādējiet savus')} <span className="text-[#1F3D2B] font-semibold">{t('portalUi.client.documents.subtitleHighlight', 'dokumentus')}</span>
        </p>
      </div>

      {/* Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#2E2E2E]/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('portalUi.client.documents.searchPlaceholder', 'Meklēt dokumentus, projektus vai autorus...')}
            className="w-full pl-9 pr-4 py-2 border border-[#1F3D2B]/20 rounded-lg text-sm"
            data-testid="client-documents-search-input"
          />
        </div>

        <div className="flex flex-wrap gap-2 overflow-x-auto pb-1">
          <Button
            variant={categoryFilter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCategoryFilter('all')} data-testid="client-documents-filter-all"
            className={categoryFilter === 'all' ? 'bg-[#1F3D2B]' : ''}
          >
            <Filter className="w-4 h-4 mr-2" />
            {t('portalUi.client.documents.filterAll', 'Visi')}
          </Button>
          {Object.entries(categoryLabels).map(([key, label]) => (
            <Button
              key={key}
              variant={categoryFilter === key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCategoryFilter(key)}
              className={categoryFilter === key ? 'bg-[#1F3D2B]' : ''}
              data-testid={`client-documents-filter-${key}`}
            >
              {label}
            </Button>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row lg:items-center gap-3">
          <div className="flex flex-col sm:flex-row flex-wrap gap-2">
            {projectOptions.length > 0 && (
              <div className="flex items-center gap-2 bg-white rounded-lg border border-[#1F3D2B]/20 px-3 py-2">
                <FolderOpen className="w-4 h-4 text-[#1F3D2B]" />
                <select
                  value={projectFilter}
                  onChange={(e) => setProjectFilter(e.target.value)}
                  className="text-sm bg-transparent outline-none"
                  data-testid="client-documents-project-filter"
                >
                  <option value="all">{t('portalUi.client.documents.allProjects', 'Visi projekti')}</option>
                  {projectOptions.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>
            )}

            {isAdmin && uploaderOptions.length > 0 && (
              <div className="flex items-center gap-2 bg-white rounded-lg border border-[#1F3D2B]/20 px-3 py-2">
                <User className="w-4 h-4 text-[#1F3D2B]" />
                <select
                  value={uploaderFilter}
                  onChange={(e) => setUploaderFilter(e.target.value)}
                  className="text-sm bg-transparent outline-none"
                  data-testid="client-documents-uploader-filter"
                >
                  <option value="all">{t('portalUi.client.documents.allUploaders', 'Visi autori')}</option>
                  {uploaderOptions.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex items-center gap-2 bg-white rounded-lg border border-[#1F3D2B]/20 px-3 py-2">
              <Tag className="w-4 h-4 text-[#1F3D2B]" />
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="text-sm bg-transparent outline-none"
                data-testid="client-documents-sort"
              >
                <option value="newest">{t('portalUi.client.documents.sortNewest', 'Jaunākie vispirms')}</option>
                <option value="oldest">{t('portalUi.client.documents.sortOldest', 'Vecākie vispirms')}</option>
              </select>
            </div>
          </div>

          <Button
            variant={showLatestOnly ? 'default' : 'outline'}
            size="sm"
            className={showLatestOnly ? 'bg-[#1F3D2B]' : ''}
            onClick={() => setShowLatestOnly((prev) => !prev)}
            data-testid="client-documents-latest-toggle"
          >
            {t('portalUi.client.documents.latestOnly', 'Tikai jaunākās versijas')}
          </Button>
        </div>
      </div>

      {/* Documents Grid */}
      {filteredDocs.length > 0 ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocs.map((doc) => {
            const FileIcon = getFileIcon(doc.file_type);
            return (
              <Card key={doc.id} className="border-0 shadow-sm hover:shadow-xl hover-lift card-shine transition-all group" data-testid={`client-document-card-${doc.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#1F3D2B]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1F3D2B] transition-colors">
                      <FileIcon className="w-6 h-6 text-[#1F3D2B] group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-[#2E2E2E] truncate" title={doc.original_filename}>
                        {doc.original_filename}
                      </h3>
                      <p className="text-sm text-[#2E2E2E]/60 mt-1">
                        {doc.file_type} • {(doc.file_size / 1024).toFixed(1)} KB
                      </p>
                      <span className="inline-block mt-2 px-2 py-1 text-xs rounded-full bg-[#1F3D2B]/10 text-[#1F3D2B]">
                        {categoryLabels[doc.category] || t('portalUi.client.documents.categoryOther', 'Citi')}
                      </span>
                      <div className="mt-3 space-y-1 text-xs text-[#2E2E2E]/60">
                        {doc.project_name && (
                          <div className="flex items-center gap-2" data-testid={`client-document-project-${doc.id}`}>
                            <FolderOpen className="w-3.5 h-3.5 text-[#1F3D2B]/60" />
                            <span>{doc.project_name}</span>
                          </div>
                        )}
                        {doc.uploaded_by_name && (
                          <div className="flex items-center gap-2" data-testid={`client-document-uploader-${doc.id}`}>
                            <User className="w-3.5 h-3.5 text-[#1F3D2B]/60" />
                            <span>{doc.uploaded_by_name}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2" data-testid={`client-document-version-${doc.id}`}>
                          <Tag className="w-3.5 h-3.5 text-[#1F3D2B]/60" />
                          <span>{t('portalUi.client.documents.versionLabel', 'Versija')} v{doc.version || 1}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap justify-between items-center gap-3">
                    <span className="text-xs text-[#2E2E2E]/40">
                      {new Date(doc.created_at).toLocaleDateString(locale)}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-[#1F3D2B] hover:bg-[#1F3D2B]/10"
                      onClick={() => handleDownload(doc)}
                      data-testid={`client-document-download-${doc.id}`}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {t('portalUi.client.documents.download', 'Lejuplādēt')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="border-0 shadow-sm card-shine" data-testid="client-documents-empty">
          <CardContent className="p-12 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-[#2E2E2E]/20" />
            <h3 className="text-lg font-semibold text-[#2E2E2E] mb-2">{t('portalUi.client.documents.emptyTitle', 'Nav dokumentu')}</h3>
            <p className="text-[#2E2E2E]/60">
              {emptyStateMessage}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DocumentsList;
